import React, { SetStateAction, useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { UserService } from '../../../../../shared/user/service';
import { ErrorToast, SuccessToast } from '../../../../../../utils/toasters';
import { ICompanyUser } from '../../../../../shared/user/dto/ICompanyUser';
import { BrandDashboardService } from '../../../../dashboard/service';
import { IAssignUserRequest } from '../../../../dashboard/dto/IAssignUserRequest';
import { User } from '../../../../../../model/user/User';
import { useTranslation } from 'react-i18next';
import { ConfirmationModal } from '../../../../../../shared/components/modal/ConfirmationModal';
import CampaignManagement from './CampaignManagement';
interface IManagementModal {
    show: boolean;
    closeModal: () => void;
    openModal: () => void;
    campaignId?: number;
    assignedUsers: User[];
    campaignName: string;
    isClientTeam?: boolean;
}

export function CampaignManagementModal({
    show,
    closeModal,
    openModal,
    campaignId,
    assignedUsers,
    campaignName,
    isClientTeam = false,
}: IManagementModal) {
    const { t } = useTranslation();
    const [companyUsers, setCompanyUsers] = useState<ICompanyUser[]>();
    const [assignUser, setAssignUser] = useState<IAssignUserRequest>({ campaignId: 0, userId: 0 });
    const [assignUsers, setAssignUsers] = useState<User[]>(assignedUsers);
    const [selectedUser, setSelectedUser] = useState<ICompanyUser>();
    const [isDeleteModalOpened, setIsDeleteModalOpened] = useState(false);

    useEffect(() => {
        if (!show) return;

        const request = isClientTeam
            ? UserService.getCompanyUsers(null, `/clients/${campaignId}`)
            : UserService.getCompanyUsers();

        request
            .then(data => {
                setCompanyUsers(data.filter(item => item.isEnabled));
            })
            .catch(error => ErrorToast(error));
    }, [show, isClientTeam]);

    useEffect(() => setAssignUser({ userId: 0, campaignId: campaignId ?? 0 }), [campaignId]);

    useEffect(() => setAssignUsers(assignedUsers), [assignedUsers]);

    function addUser() {
        BrandDashboardService.assignUserToCampaign(assignUser)
            .then(response => {
                SuccessToast(t('general.toasts.successAssignedUser'));
                setAssignUser(prev => ({ ...prev, userId: 0 }));
                setSelectedUser(undefined);
                setAssignUsers(response.assignedUsers);
            })
            .catch(error => ErrorToast(error));
    }

    function deleteUser(id: number) {
        BrandDashboardService.deleteUserFromCampaign({ ...assignUser, userId: id })
            .then(response => {
                SuccessToast(t('general.toasts.successRemovedUser'));
                setAssignUsers(response.assignedUsers);
            })
            .catch(error => ErrorToast(error));
    }

    useEffect(() => {
        if (!!selectedUser?.id) {
            if (isDeleteModalOpened) {
                closeModal();
            } else {
                openModal();
            }
        }
    }, [isDeleteModalOpened]);

    return (
        <>
            <Modal he centered show={show} onHide={closeModal}>
                <Modal.Body className="px-1">
                    <div className="mb-3 px-3">
                        <h4 className="mb-0">
                            {isClientTeam
                                ? t('brand.campaign.dashboard.modal.tabs.clientCampaignManagement')
                                : t('brand.campaign.dashboard.modal.tabs.campaignManagement')}
                        </h4>
                        <h6 className="text-muted mb-0">{campaignName}</h6>
                    </div>
                    <CampaignManagement
                        setIsDeleteModalOpened={setIsDeleteModalOpened}
                        companyUsers={companyUsers}
                        setAssignUser={setAssignUser}
                        selectedUser={selectedUser}
                        setSelectedUser={setSelectedUser as React.Dispatch<SetStateAction<ICompanyUser>>}
                        assignUser={assignUser}
                        assignUsers={assignUsers}
                        isClientTeam={isClientTeam}
                    />
                </Modal.Body>
                <Modal.Footer className="mt-3 mt-md-0">
                    <Button variant="outline-primary" onClick={closeModal}>
                        {t('general.button.cancel')}
                    </Button>
                    <Button variant="primary" onClick={addUser} disabled={!assignUser?.userId}>
                        {t('general.button.save')}
                    </Button>
                </Modal.Footer>
            </Modal>
            <ConfirmationModal
                title={t('general.removeUser')}
                description={`${t('general.confirmRemoveUser')} ${
                    selectedUser?.displayName ?? selectedUser?.firstName
                }`}
                action={() => deleteUser(Number(selectedUser?.id))}
                show={isDeleteModalOpened}
                closeModal={() => setIsDeleteModalOpened(false)}
            />
        </>
    );
}
